// The Story room's timeline and chronology data (Apps/Shared/StoryCharts.swift and Rooms/StoryRoom.swift: `storyScenes`,
// `TimeVsOrderChart`, `CharacterLanesChart`, `ChronologyChart`, `CalendarLanes`) and SuiteStory/CoPresence.swift. Data
// only: the app draws it.
import type { EntityKind } from '../contract.ts';
import type { StoryEntity } from '../model/documents.ts';
import { scenes as selectScenes, type SelectionEntry } from '../model/manuscriptSelection.ts';
import { swiftCompare } from '../model/order.ts';
import { StandardField, type OpenedProject } from '../project.ts';
import { wordCount } from '../proseMarkdown.ts';
import { reference, type PresenceLevel, type PresenceRow } from '../presence.ts';

// MARK: Scenes in order

/** `StoryScene`: a scene of the arrangement in narrative order, with its story data. */
export interface StoryScene {
  id: string;
  /** 1-based place in the arrangement. */
  position: number;
  title: string;
  storyTime: number | null;
  duration: number;
  pov: string | null;
  setting: string | null;
  /** Filled in between tagged neighbours (spec §10.1: partial data is normal, and labelled). */
  estimatedTime: number | null;
}

/** `StoryScene.time`: the story time, else the estimate. */
export const sceneTime = (s: StoryScene) => s.storyTime ?? s.estimatedTime;

/** `LibraryModel.sceneKinds`: a screenplay's scenes are Fountain, everything else's prose. */
export const sceneKinds = (project: OpenedProject) => [project.record.projectType === 'screenplay' ? 'screenplay' as const : 'prose' as const];

/** `LibraryModel.storyScenes`: the arrangement's scenes (the manuscript unless another is named; a folder only with
 *  words of its own) in narrative order, with story times estimated where missing by interpolating between the nearest
 *  tagged scenes before and after. */
export function storyScenes(project: OpenedProject, arrangementID?: string): StoryScene[] {
  const record = project.record;
  const arrangement = arrangementID !== undefined
    ? (record.arrangements.find((a) => a.id === arrangementID) ?? null)
    : (record.arrangements.find((a) => a.role === 'manuscript') ?? null);
  if (!arrangement) return [];
  const entries = new Map<string, SelectionEntry>([...record.fragments].map(([id, e]) => [id, { kind: e.kind, trashed: e.trashed }]));
  const ids = selectScenes(arrangement, entries, sceneKinds(project), project.arrangementTree(arrangement.id))
    .filter((s) => { const f = project.fragmentRecord(s.id); return !s.isFolder || (f ? wordCount(f.text, f.kind) : 0) > 0; })
    .map((s) => s.id);
  const scenes: StoryScene[] = ids.map((id, i) => {
    const f = project.fragmentRecord(id);
    const time = f?.metadata[StandardField.storyTime];
    const duration = f?.metadata[StandardField.storyDuration];
    return {
      id, position: i + 1, title: f?.title ?? 'Untitled',
      storyTime: time?.type === 'date' ? time.value.getTime() : null,
      duration: duration?.type === 'number' ? duration.value : 1,
      pov: f ? reference(f.metadata, StandardField.pov) : null,
      setting: f ? reference(f.metadata, StandardField.setting) : null,
      estimatedTime: null,
    };
  });
  const tagged = scenes.map((s, i) => (s.storyTime !== null ? i : -1)).filter((i) => i >= 0);
  scenes.forEach((s, i) => {
    if (s.storyTime !== null) return;
    const before = [...tagged].reverse().find((t) => t < i), after = tagged.find((t) => t > i);
    if (before === undefined || after === undefined) return;
    const a = scenes[before].storyTime!, b = scenes[after].storyTime!;
    const t = (i - before) / (after - before);
    s.estimatedTime = a + (b - a) * t;
  });
  return scenes;
}

const entityName = (entities: readonly StoryEntity[], id: string | null) => (id === null ? undefined : entities.find((e) => e.id === id)?.name);

// MARK: Story time against the order of telling (TimeVsOrderChart)

export interface TimeOrderPoint { sceneID: string; position: number; time: number; estimated: boolean; strand: string }

/** The points of the time-vs-order chart: scenes with a time, each on its POV's strand ("No POV" without one). */
export function timeVersusOrder(scenes: StoryScene[], entities: readonly StoryEntity[]): TimeOrderPoint[] {
  return scenes.filter((s) => sceneTime(s) !== null).map((s) => ({
    sceneID: s.id, position: s.position, time: sceneTime(s)!, estimated: s.storyTime === null, strand: entityName(entities, s.pov) ?? 'No POV',
  }));
}

// MARK: Presence by reading order (CharacterLanesChart)

export interface LaneMark { id: string; entityID: string; entity: string; position: number; level: PresenceLevel; sceneID: string }
export interface LaneGap { id: string; entityID: string; entity: string; start: number; end: number }

/** The reading-order lanes: a mark per scene an entity of `kinds` is in, lane names in order of first appearance, and
 *  (for characters) gaps where one is absent for more than `absenceThreshold` scenes. */
export function readingOrderLanes(scenes: StoryScene[], presence: readonly PresenceRow[], entities: readonly StoryEntity[],
  kinds: readonly EntityKind[] = ['character'], absenceThreshold = 10): { marks: LaneMark[]; names: string[]; gaps: LaneGap[] } {
  const position = new Map<string, number>();
  for (const s of scenes) if (!position.has(s.id)) position.set(s.id, s.position);
  const shown = entities.filter((e) => kinds.includes(e.kind));
  const marks: LaneMark[] = [];
  for (const p of presence) {
    const pos = position.get(p.fragmentID);
    const e = shown.find((x) => x.id === p.entityID);
    if (pos === undefined || !e) continue;
    marks.push({ id: p.fragmentID + p.entityID, entityID: e.id, entity: e.name, position: pos, level: p.level, sceneID: p.fragmentID });
  }
  const names: string[] = [];
  for (const m of [...marks].sort((a, b) => a.position - b.position)) if (!names.includes(m.entity)) names.push(m.entity);
  const gaps: LaneGap[] = [];
  if (kinds.includes('character')) {
    for (const e of shown) {
      const positions = marks.filter((m) => m.entity === e.name).map((m) => m.position).sort((a, b) => a - b);
      for (let i = 0; i + 1 < positions.length; i++) {
        const [a, b] = [positions[i], positions[i + 1]];
        if (b - a > absenceThreshold) gaps.push({ id: `${e.id}-${a}`, entityID: e.id, entity: e.name, start: a, end: b });
      }
    }
  }
  return { marks, names, gaps };
}

// MARK: Chronology (ChronologyChart)

export type ChronologyGrouping = 'pov' | 'setting' | 'character';

export interface ChronologyBar { id: string; scene: StoryScene; lane: string }

export interface Chronology {
  /** The scenes shown, by story time. */
  scenes: StoryScene[];
  bars: ChronologyBar[];
  /** Scene → its lanes. */
  lanes: Map<string, string[]>;
  /** Lane name → the entity's colour ("r g b"), where the lane is an entity. */
  colors: Map<string, string | undefined>;
}

/** The chronology: scenes on a calendar axis (by story time; estimated ones too when `showEstimated`) in lanes by POV,
 *  setting or character (with characters, a scene is in the lane of everyone present, mentions included, as that's all
 *  an untagged project knows). */
export function chronology(scenes: StoryScene[], presence: readonly PresenceRow[], entities: readonly StoryEntity[],
  grouping: ChronologyGrouping, showEstimated = true): Chronology {
  const shown = scenes.filter((s) => s.storyTime !== null || (showEstimated && sceneTime(s) !== null))
    .map((s, i) => [s, i] as const).sort((a, b) => sceneTime(a[0])! - sceneTime(b[0])! || a[1] - b[1]).map(([s]) => s);
  const present = new Map<string, string[]>();
  if (grouping === 'character') {
    for (const p of presence) {
      if (p.level === 'absent') continue;
      const e = entities.find((x) => x.id === p.entityID);
      if (e && e.kind === 'character') present.set(p.fragmentID, [...(present.get(p.fragmentID) ?? []), e.name]);
    }
  }
  const lanesOf = (s: StoryScene): string[] => {
    switch (grouping) {
      case 'pov': return [entityName(entities, s.pov) ?? 'No POV'];
      case 'setting': return [entityName(entities, s.setting) ?? 'No Setting'];
      case 'character': {
        const names = present.get(s.id) ?? [];
        return names.length ? [...new Set(names)].sort(swiftCompare) : ['No Character'];
      }
    }
  };
  const lanes = new Map<string, string[]>();
  for (const s of shown) lanes.set(s.id, lanesOf(s));
  const bars = shown.flatMap((s) => (lanes.get(s.id) ?? []).map((lane) => ({ id: `${s.id}|${lane}`, scene: s, lane })));
  const colors = new Map<string, string | undefined>();
  for (const e of entities) if (!colors.has(e.name)) colors.set(e.name, e.color);
  return { scenes: shown, bars, lanes, colors };
}

// MARK: Calendar lanes (CalendarLanes)

export interface CalendarMark { id: string; scene: StoryScene; lane: string; pov: boolean }
export interface CalendarLink { id: string; scene: StoryScene; top: string; bottom: string }

export interface CalendarLanes {
  /** Scenes with a time (story or estimated), in narrative order. */
  scenes: StoryScene[];
  /** Character ids, most present first (at most ten, so lanes stay readable), numbered from the top. */
  lanes: string[];
  /** A dot per character per scene; filled (`pov`) for the point of view. */
  marks: CalendarMark[];
  /** A line per scene joining everyone in it (top lane to bottom lane), where two or more are. */
  links: CalendarLink[];
}

/** Scene → the entities present in it (not absent). */
export function presentByScene(presence: readonly PresenceRow[]): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const p of presence) {
    if (p.level === 'absent') continue;
    let set = out.get(p.fragmentID);
    if (!set) out.set(p.fragmentID, (set = new Set()));
    set.add(p.entityID);
  }
  return out;
}

/** `CalendarLanes`: the Story ▸ Timeline page's data. */
export function calendarLanes(scenes: StoryScene[], presence: readonly PresenceRow[], entities: readonly StoryEntity[], maxLanes = 10): CalendarLanes {
  const timed = scenes.filter((s) => sceneTime(s) !== null);
  const present = presentByScene(presence);
  const characters = new Set(entities.filter((e) => e.kind === 'character').map((e) => e.id));
  const counts = new Map<string, number>();
  for (const s of timed) for (const id of present.get(s.id) ?? []) if (characters.has(id)) counts.set(id, (counts.get(id) ?? 0) + 1);
  // `sorted { ($0.value, $1.key) > ($1.value, $0.key) }`: most present first, ties by id.
  const lanes = [...counts].sort((a, b) => b[1] - a[1] || swiftCompare(a[0], b[0])).slice(0, maxLanes).map(([id]) => id);
  const marks = timed.flatMap((s) => [...(present.get(s.id) ?? [])].filter((id) => lanes.includes(id))
    .map((lane) => ({ id: `${s.id}|${lane}`, scene: s, lane, pov: lane === s.pov })));
  const links: CalendarLink[] = [];
  for (const s of timed) {
    const inScene = lanes.filter((id) => present.get(s.id)?.has(id));
    if (inScene.length > 1) links.push({ id: s.id, scene: s, top: inScene[0], bottom: inScene[inScene.length - 1] });
  }
  return { scenes: timed, lanes, marks, links };
}

// MARK: Co-presence (SuiteStory/CoPresence.swift)

/** Who shares scenes with whom, for the timeline and character studies: the manuscript's scenes in reading order and
 *  the entities present in each (absent ones left out; mentions count, since that's all an untagged project knows). */
export class CoPresence {
  readonly present = new Map<string, Set<string>>();

  constructor(readonly order: readonly string[], presence: Iterable<{ scene: string; entity: string; level: PresenceLevel }>) {
    const scenes = new Set(order);
    for (const row of presence) {
      if (row.level === 'absent' || !scenes.has(row.scene)) continue;
      let set = this.present.get(row.scene);
      if (!set) this.present.set(row.scene, (set = new Set()));
      set.add(row.entity);
    }
  }

  /** From the index's presence rows. */
  static fromRows(order: readonly string[], rows: readonly PresenceRow[]): CoPresence {
    return new CoPresence(order, rows.map((r) => ({ scene: r.fragmentID, entity: r.entityID, level: r.level })));
  }

  /** Whether the entity is in each scene, in reading order (the character study's strip). */
  strip(entity: string): boolean[] {
    return this.order.map((s) => this.present.get(s)?.has(entity) === true);
  }

  /** How many scenes the entity is in. */
  scenes(entity: string): number {
    return this.order.filter((s) => this.present.get(s)?.has(entity) === true).length;
  }

  /** How many scenes two entities are both in. */
  shared(a: string, b: string): number {
    return this.order.filter((s) => { const p = this.present.get(s); return !!p && p.has(a) && p.has(b); }).length;
  }

  /** Everyone who shares a scene with the entity, most shared first (ties in `candidates` order). */
  partners(entity: string, candidates: readonly string[]): { entity: string; scenes: number }[] {
    return candidates.filter((c) => c !== entity).map((c) => ({ entity: c, scenes: this.shared(entity, c) })).filter((p) => p.scenes > 0)
      .map((p, i) => [p, i] as const).sort((a, b) => b[0].scenes - a[0].scenes || a[1] - b[1]).map(([p]) => p);
  }
}

/** `storyAppearances`: manuscript fragments each entity appears in (any presence) and those told from its POV. */
export function storyAppearances(project: OpenedProject, presence: readonly PresenceRow[]): Map<string, { scenes: number; pov: number }> {
  const manuscript = project.record.arrangements.find((a) => a.role === 'manuscript');
  const out = new Map<string, { scenes: number; pov: number }>();
  if (!manuscript) return out;
  const inManuscript = new Set([...manuscript.nodes.values()].map((n) => n.fragmentID).filter((id) => id !== undefined));
  const scenes = new Map<string, Set<string>>();
  for (const row of presence) {
    if (row.level === 'absent' || !inManuscript.has(row.fragmentID)) continue;
    let set = scenes.get(row.entityID);
    if (!set) scenes.set(row.entityID, (set = new Set()));
    set.add(row.fragmentID);
    const entry = out.get(row.entityID) ?? { scenes: 0, pov: 0 };
    if (row.level === 'pov') entry.pov += 1;
    out.set(row.entityID, entry);
  }
  for (const [id, set] of scenes) out.get(id)!.scenes = set.size;
  return out;
}

/** `storyPeople`: the Story column's characters, most present in the manuscript first (ties by name). */
export function storyPeople(project: OpenedProject, presence: readonly PresenceRow[]): StoryEntity[] {
  const appearances = storyAppearances(project, presence);
  return project.record.entities.filter((e) => e.kind === 'character')
    .map((e) => [e, appearances.get(e.id)?.scenes ?? 0] as const)
    .sort((a, b) => b[1] - a[1] || swiftCompare(a[0].name, b[0].name))
    .map(([e]) => e);
}

/** The manuscript's `CoPresence` (`LibraryModel.coPresence`): its scenes in reading order (a folder only with words of
 *  its own) and everyone present in each. */
export function manuscriptCoPresence(project: OpenedProject, presence: readonly PresenceRow[]): CoPresence {
  return CoPresence.fromRows(storyScenes(project).map((s) => s.id), presence);
}
